import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Card, H5, XStack, YStack, SizableText } from 'tamagui';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatCurrency, formatNumber, formatPercentage } from '@utils/formatters';

interface SalesStatsOverviewProps {
  stats: {
    overview: {
      allTime: {
        totalRevenue: number;
        totalTransactions: number;
        averageTransaction: number;
        successRate: number;
      };
      thisMonth: {
        totalRevenue: number;
        totalTransactions: number;
        averageTransaction: number;
        successRate: number;
      };
      lastMonth: {
        totalRevenue: number;
        totalTransactions: number;
        averageTransaction: number;
        successRate: number;
      };
      monthOverMonthGrowth: number;
    };
  };
  isLoading?: boolean;
}

const SalesStatsOverview: React.FC<SalesStatsOverviewProps> = ({ stats, isLoading }) => {
  const theme = themeSelector();

  if (isLoading) {
    return (
      <Card padding="$4" backgroundColor={theme ? "#ffffff" : "#1e293b"}>
        <YStack space="$3" alignItems="center">
          <SizableText>Loading sales statistics...</SizableText>
        </YStack>
      </Card>
    );
  }

  // Handle case where stats is undefined or doesn't have the expected structure
  if (!stats || !stats.overview) {
    return (
      <Card padding="$4" backgroundColor={theme ? "#ffffff" : "#1e293b"}>
        <YStack space="$3" alignItems="center">
          <SizableText>No sales data available</SizableText>
        </YStack>
      </Card>
    );
  }

  const { overview } = stats;
  const isGrowthPositive = overview.monthOverMonthGrowth >= 0;

  const StatCard = ({ title, value, subtitle, icon, color = theme ? "#374151" : "#e5e7eb" }) => (
    <Card
      padding="$3"
      backgroundColor={theme ? "#f9fafb" : "#374151"}
      borderRadius="$4"
      flex={1}
      minHeight={100}
    >
      <YStack space="$2">
        <XStack alignItems="center" space="$2">
          <MaterialIcons name={icon} size={20} color={color} />
          <SizableText size="$3" color={theme ? "#6b7280" : "#9ca3af"}>
            {title}
          </SizableText>
        </XStack>
        <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
          {value}
        </H5>
        {subtitle && (
          <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"}>
            {subtitle}
          </SizableText>
        )}
      </YStack>
    </Card>
  );

  const GrowthIndicator = ({ growth }) => (
    <XStack alignItems="center" space="$1">
      <AntDesign
        name={growth >= 0 ? "arrow-up" : "arrowdown"}
        size={14}
        color={growth >= 0 ? "#10b981" : "#ef4444"}
      />
      <SizableText
        size="$3"
        color={growth >= 0 ? "#10b981" : "#ef4444"}
        fontWeight="600"
      >
        {formatPercentage(Math.abs(growth))}
      </SizableText>
    </XStack>
  );

  return (
    <Card
      padding="$4"
      backgroundColor={theme ? "#ffffff" : "#1e293b"}
      borderRadius={20}
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 12 }}
      shadowOpacity={theme ? 0.08 : 0.25}
      shadowRadius={24}
      elevation={12}
      borderWidth={1}
      borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
      overflow="hidden"
    >
      <YStack space="$4">
        {/* Header */}
        <XStack justifyContent="space-between" alignItems="center">
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Sales Overview
          </H5>
          <XStack alignItems="center" space="$2">
            <SizableText size="$3" color={theme ? "#6b7280" : "#9ca3af"}>
              vs last month
            </SizableText>
            <GrowthIndicator growth={overview.monthOverMonthGrowth} />
          </XStack>
        </XStack>

        {/* Primary Stats */}
        <XStack space="$3">
          <StatCard
            title="This Month"
            value={formatCurrency(overview.thisMonth.totalRevenue)}
            subtitle={`${formatNumber(overview.thisMonth.totalTransactions)} transactions`}
            icon="trending-up"
            color="#10b981"
          />
          <StatCard
            title="All Time"
            value={formatCurrency(overview.allTime.totalRevenue)}
            subtitle={`${formatNumber(overview.allTime.totalTransactions)} transactions`}
            icon="show-chart"
            color="#3b82f6"
          />
        </XStack>

        {/* Secondary Stats */}
        <XStack space="$3">
          <StatCard
            title="Avg Transaction"
            value={formatCurrency(overview.thisMonth.averageTransaction)}
            subtitle="This month"
            icon="receipt"
          />
          <StatCard
            title="Success Rate"
            value={formatPercentage(overview.thisMonth.successRate)}
            subtitle="This month"
            icon="check-circle"
            color="#10b981"
          />
        </XStack>

        {/* Additional Info */}
        <Card
          padding="$3"
          backgroundColor={theme ? "#ffffff" : "#1e293b"}
          borderRadius="$3"
        >
          <XStack justifyContent="space-between" alignItems="center">
            <YStack>
              <SizableText
                size="$3"
                color={isGrowthPositive ? "#166534" : "#991b1b"}
                fontWeight="600"
              >
                {isGrowthPositive ? "Revenue Growth" : "Revenue Decline"}
              </SizableText>
              <SizableText size="$2" color={isGrowthPositive ? "#15803d" : "#dc2626"}>
                {isGrowthPositive
                  ? `Up ${formatPercentage(overview.monthOverMonthGrowth)} from last month`
                  : `Down ${formatPercentage(Math.abs(overview.monthOverMonthGrowth))} from last month`
                }
              </SizableText>
            </YStack>
            <MaterialIcons
              name={isGrowthPositive ? "trending-up" : "trending-down"}
              size={24}
              color={isGrowthPositive ? "#16a34a" : "#dc2626"}
            />
          </XStack>
        </Card>
      </YStack>
    </Card>
  );
};

export default SalesStatsOverview;
